import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiAccountMultiplePlusOutline,
    mdiDownload,
    mdiUpload,
    mdiCheck,
    mdiClose,
    mdiAlertOutline,
    mdiAccountCheck,
} from "@mdi/js";
import { toast } from "react-toastify";
import AppHeader from "../../components/AppHeader";
import BaseModal from "../../components/BaseModal";
import EmailReviewModal from "../../components/communications/EmailReviewModal";
import { classApi } from "../../api/classes";
import { organizationApi } from "../../api/organization";
import { useAuth } from "../../contexts/AuthContext";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassStudentImportPage.css";

function isDlAccountRow(row) {
    return row.status === "duplicate" && row.existingSource === "account";
}

function parseCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/\s+/g, "_"));
    return lines
        .slice(1)
        .map(line => {
            const values = line.split(",").map(v => v.trim());
            const row = {};
            headers.forEach((h, i) => {
                row[h] = values[i] || "";
            });
            return {
                firstName: row["first_name"] || "",
                lastName: row["last_name"] || "",
                email: row["email"] || "",
                phone: row["phone"] || "",
                classCode: row["class_code"] || "",
                paymentStatus: row["payment_status"] || "unpaid",
            };
        })
        .filter(row => row.firstName);
}

function downloadTemplate() {
    const csv =
        "first_name,last_name,email,phone,class_code,payment_status\n" +
        "Jane,Smith,jane@example.com,0400123456,BEG1,paid\n" +
        "John,Doe,john@example.com,,BEG1,unpaid\n" +
        "Sam,Jones,sam@example.com,,,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dancelocale-student-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
}

const ClassStudentImportPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState("mapping");
    const [parsedRows, setParsedRows] = useState([]);
    const [checkedRows, setCheckedRows] = useState([]);
    const [results, setResults] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const fileInputRef = useRef(null);

    // Code Mapping (Step 1)
    const [mappings, setMappings] = useState([{ schoolCode: "", classId: "" }]);
    const [availableClasses, setAvailableClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [showEmptyMappingModal, setShowEmptyMappingModal] = useState(false);
    const [pendingRows, setPendingRows] = useState(null);
    const [csvHasClassCode, setCsvHasClassCode] = useState(false);
    const [orgName, setOrgName] = useState("");
    const [currentTermName, setCurrentTermName] = useState("");

    // Post-import invite modal state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [invitableProfileIds, setInvitableProfileIds] = useState([]);
    const [isConfirmingBulkInvite, setIsConfirmingBulkInvite] = useState(false);

    useEffect(() => {
        setLoadingClasses(true);
        const orgId = user?.orgId;

        const loadData = async () => {
            const [termsRes, org] = await Promise.all([
                classApi.getTerms(),
                orgId ? organizationApi.getOrgProfile(orgId).catch(() => null) : Promise.resolve(null),
            ]);
            if (org?.name) setOrgName(org.name);

            // Find current term: started and not yet ended (API returns newest first)
            const terms = termsRes.ok ? termsRes.data.terms || [] : [];
            const now = new Date();
            const currentTerm =
                terms.find(t => !t.isPast && t.startDate && new Date(t.startDate) <= now) || terms.find(t => !t.isPast); // fallback: next upcoming term
            if (currentTerm) {
                setCurrentTermName(currentTerm.name || "");
                const classRes = await classApi.getClasses(currentTerm.id);
                if (classRes.ok) {
                    const sorted = [...(classRes.data.classes || [])].sort((a, b) =>
                        (a.name || "").localeCompare(b.name || "")
                    );
                    setAvailableClasses(sorted);
                }
            }
        };

        loadData()
            .catch(() => {
                /* non-fatal */
            })
            .finally(() => setLoadingClasses(false));
    }, [user]);

    function getMappingDict() {
        return Object.fromEntries(
            mappings
                .filter(m => m.schoolCode.trim() && m.classId)
                .map(m => [m.schoolCode.trim().toLowerCase(), m.classId])
        );
    }

    function resolveRowEnrolment(row) {
        if (!row.classCode) return "no-code";
        const dict = getMappingDict();
        return dict[row.classCode.toLowerCase()] ? "mapped" : "unmapped";
    }

    function toggleRow(i) {
        setCheckedRows(prev => (prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]));
    }

    function toggleAll() {
        const selectableIndices = parsedRows
            .map((r, i) => (isDlAccountRow(r) || r.status !== "duplicate" ? i : null))
            .filter(i => i !== null);
        const allSelected = selectableIndices.every(i => checkedRows.includes(i));
        setCheckedRows(allSelected ? [] : selectableIndices);
    }

    async function handleFileChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.endsWith(".csv")) {
            toast.error("Please upload a .csv file");
            return;
        }
        const text = await file.text();
        const rows = parseCSV(text);
        if (rows.length === 0) {
            toast.error(
                "No valid rows found. Make sure the file has a header row and at least one student with a first name."
            );
            return;
        }
        if (rows.length > 500) {
            toast.error("Maximum 500 students per upload. Please split your file into smaller batches.");
            return;
        }

        // Detect class_code column
        const firstLine = text.trim().split(/\r?\n/)[0];
        const headers = firstLine.split(",").map(h => h.trim().toLowerCase());
        const hasClassCode = headers.includes("class_code");
        setCsvHasClassCode(hasClassCode);

        // If CSV has class codes but no mappings configured, pause and ask
        const effectiveMappings = getMappingDict();
        if (hasClassCode && Object.keys(effectiveMappings).length === 0) {
            setPendingRows(rows);
            setShowMappingModal(true);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        await processRows(rows);
    }

    async function processRows(rows) {
        setIsChecking(true);
        try {
            const res = await classApi.bulkCheckDuplicates(
                rows.map(r => ({ firstName: r.firstName, lastName: r.lastName, email: r.email }))
            );
            if (!res.ok) {
                toast.error(res.data?.message || "Failed to check duplicates");
                return;
            }
            const withStatus = rows.map((row, i) => ({
                ...row,
                ...res.data.students[i],
            }));
            setParsedRows(withStatus);
            setCheckedRows(
                withStatus.map((r, i) => (r.status === "new" || isDlAccountRow(r) ? i : null)).filter(i => i !== null)
            );
            setStep("preview");
        } catch {
            toast.error("Failed to check for duplicates. Please try again.");
        } finally {
            setIsChecking(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    function handleMappingModalBack() {
        setShowMappingModal(false);
        setPendingRows(null);
        setStep("mapping");
    }

    async function handleMappingModalContinue() {
        setShowMappingModal(false);
        const rows = pendingRows;
        setPendingRows(null);
        await processRows(rows);
    }

    async function handleImport() {
        const toImport = checkedRows.map(i => parsedRows[i]).filter(Boolean);
        if (toImport.length === 0) {
            toast.info("No students selected to import.");
            return;
        }
        setStep("importing");

        const dict = getMappingDict();
        const enrolments = toImport
            .map((row, idx) => {
                if (!row.classCode) return null;
                const classId = dict[row.classCode.toLowerCase()];
                if (!classId) return null;
                return {
                    studentIndex: idx,
                    classId,
                    paymentStatus: row.paymentStatus === "paid" ? "paid" : "unpaid",
                };
            })
            .filter(Boolean);

        try {
            const res = await classApi.bulkImportStudents(toImport, enrolments);
            if (!res.ok) {
                toast.error(res.data?.message || "Import failed");
                setStep("preview");
                return;
            }
            const skippedCount = parsedRows.length - toImport.length;
            setResults({
                importedCount: res.data.importedCount,
                skippedCount,
                enrolledCount: res.data.enrolledCount || 0,
                unmappedCodes: res.data.unmappedCodes || [],
            });
            if (res.data.newProfileIds && res.data.newProfileIds.length > 0) {
                setInvitableProfileIds(res.data.newProfileIds);
                setShowInviteModal(true);
            }
            setStep("results");
        } catch {
            toast.error("Import failed. Please try again.");
            setStep("preview");
        }
    }

    // Confirm signature is no-arg ({}) — Mode 2b is a locked template, the
    // confirmation contract is "yes, send to N recipients" with no editable fields.
    async function handleSendImportInvites() {
        const ids = invitableProfileIds;
        setIsConfirmingBulkInvite(true);
        try {
            const res = await classApi.bulkSendInvites(ids);
            if (res.ok) {
                toast.success(`Sent ${res.data.sent} invite${res.data.sent !== 1 ? "s" : ""}`);
            } else {
                toast.error(res.data?.message || "Failed to send invites");
            }
        } catch {
            toast.error("Failed to send invites");
        } finally {
            setIsConfirmingBulkInvite(false);
            setShowInviteModal(false);
            setInvitableProfileIds([]);
        }
    }

    return (
        <div className="dashboard-container org-console class-student-import-page">
            <AppHeader />
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate("/classes/students")} className="back-nav-btn">
                                ← Students
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon
                                    path={mdiAccountMultiplePlusOutline}
                                    size={1}
                                    style={{ marginRight: 8, verticalAlign: "middle" }}
                                />
                                Import Students
                            </h1>
                        </div>
                        <p className="sub-portal-header-subtitle">
                            Upload a CSV file to bulk-import your existing students
                        </p>
                    </div>

                    {step === "mapping" && (
                        <div className="content-card import-step-card">
                            <h2>Step 1: Download the template</h2>
                            <div className="import-template-section">
                                <p>
                                    Download the CSV template, fill it in using any spreadsheet app (Excel, Google
                                    Sheets), then export as CSV. The template includes columns:{" "}
                                    <strong>first_name, last_name, email, phone</strong>.
                                </p>
                                <p>
                                    <strong>Optional:</strong> include <strong>class_code</strong> and{" "}
                                    <strong>payment_status</strong> (paid/unpaid) columns, to automatically enrol
                                    students into classes during import. Create and map those codes in{" "}
                                    <strong>Step 2</strong> below.
                                </p>
                                <p>
                                    <strong>Tip:</strong> avoid commas inside name fields for best results. If opening
                                    in Excel, click <strong>Don't Convert</strong> when prompted, this preserves phone
                                    numbers with leading zeros (e.g. 0400123456).
                                </p>
                                <button className="btn-purple" onClick={downloadTemplate}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <Icon path={mdiDownload} size={0.8} />
                                        Download CSV Template
                                    </span>
                                </button>
                            </div>

                            <h2>Step 2: Map your class codes (optional - must setup term classes first)</h2>
                            <p className="import-step-description">
                                If you want to also enrol students when you upload them, you can add a{" "}
                                <strong>class_code</strong> column to your spreadsheet, enter each code below as shown
                                in your CSV and select the matching term class.
                            </p>
                            <p className="import-step-description">
                                Skip this step if you do not wish to match class codes.
                                <strong> Note:</strong> skipping means each student will need to be enrolled separately
                                in the students page.
                            </p>
                            {loadingClasses ? (
                                <p className="import-checking-text">Loading your classes...</p>
                            ) : (
                                <>
                                    <div className="import-mapping-table">
                                        <div className="import-mapping-header">
                                            <span>Your code</span>
                                            <span>
                                                {orgName ? `${orgName} class` : "Your school's class"}
                                                {currentTermName ? ` (${currentTermName})` : ""}
                                            </span>
                                        </div>
                                        {mappings.map((m, i) => (
                                            <div key={i} className="import-mapping-row">
                                                <input
                                                    className="import-mapping-code-input"
                                                    type="text"
                                                    placeholder="e.g. BEG1"
                                                    value={m.schoolCode}
                                                    onChange={e => {
                                                        const next = [...mappings];
                                                        next[i] = { ...next[i], schoolCode: e.target.value };
                                                        setMappings(next);
                                                    }}
                                                />
                                                <select
                                                    className="import-mapping-class-select"
                                                    value={m.classId}
                                                    onChange={e => {
                                                        const next = [...mappings];
                                                        next[i] = { ...next[i], classId: e.target.value };
                                                        setMappings(next);
                                                    }}>
                                                    <option value="">
                                                        Select class from {currentTermName || "current term"}
                                                    </option>
                                                    {availableClasses.map(cls => (
                                                        <option key={cls.id} value={cls.id}>
                                                            {cls.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {mappings.length > 1 && (
                                                    <button
                                                        className="import-mapping-remove-btn"
                                                        onClick={() => setMappings(mappings.filter((_, j) => j !== i))}
                                                        title="Remove row">
                                                        <Icon path={mdiClose} size={0.7} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            className="btn-turquoise btn-small"
                                            onClick={() => setMappings([...mappings, { schoolCode: "", classId: "" }])}>
                                            + Add row
                                        </button>
                                    </div>
                                    <div className="import-mapping-actions">
                                        <button className="btn-gray" onClick={() => setStep("upload")}>
                                            Skip — no class codes in my CSV
                                        </button>
                                        <button
                                            className="btn-purple"
                                            onClick={() => {
                                                if (Object.keys(getMappingDict()).length === 0) {
                                                    setShowEmptyMappingModal(true);
                                                    return;
                                                }
                                                setStep("upload");
                                            }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                Continue with these mappings →
                                            </span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {step === "upload" && (
                        <div className="content-card import-step-card">
                            <button className="back-nav-btn" onClick={() => setStep("mapping")}>
                                ← Steps 1 & 2
                            </button>
                            <h2>Step 3: Upload your CSV</h2>
                            <div className="import-upload-zone" onClick={() => fileInputRef.current?.click()}>
                                <Icon path={mdiUpload} size={1.5} />
                                <p>Click to select your CSV file</p>
                                <p className="upload-hint">Maximum 500 students per upload</p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    hidden
                                />
                            </div>
                            {isChecking && <p className="import-checking-text">Checking for duplicates...</p>}
                        </div>
                    )}

                    {step === "preview" && (
                        <div className="content-card import-step-card">
                            <div className="import-preview-header">
                                <h2>
                                    {parsedRows.length} student{parsedRows.length !== 1 ? "s" : ""} found
                                </h2>
                                <div style={{ display: "flex", gap: 10 }}>
                                    <button className="btn-turquoise btn-small" onClick={() => setStep("upload")}>
                                        Upload different file
                                    </button>
                                    <button
                                        className="btn-purple btn-small"
                                        onClick={handleImport}
                                        disabled={checkedRows.length === 0}>
                                        Import {checkedRows.length} student{checkedRows.length !== 1 ? "s" : ""}
                                    </button>
                                </div>
                            </div>
                            <div className="import-preview-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    title="Select all selectable rows"
                                                    onChange={toggleAll}
                                                    checked={
                                                        parsedRows.filter(
                                                            r => isDlAccountRow(r) || r.status !== "duplicate"
                                                        ).length > 0 &&
                                                        parsedRows
                                                            .map((r, i) =>
                                                                isDlAccountRow(r) || r.status !== "duplicate" ? i : null
                                                            )
                                                            .filter(i => i !== null)
                                                            .every(i => checkedRows.includes(i))
                                                    }
                                                />
                                            </th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Class</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedRows.map((row, i) => {
                                            const enrolmentStatus = resolveRowEnrolment(row);
                                            const dict = getMappingDict();
                                            return (
                                                <tr
                                                    key={i}
                                                    className={[
                                                        row.status === "duplicate" && !isDlAccountRow(row)
                                                            ? "row-duplicate"
                                                            : "",
                                                        enrolmentStatus === "mapped" ? "row-enrolment-mapped" : "",
                                                        enrolmentStatus === "unmapped" ? "row-unmapped-code" : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ")}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={checkedRows.includes(i)}
                                                            onChange={() => toggleRow(i)}
                                                            disabled={
                                                                row.status === "duplicate" && !isDlAccountRow(row)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        {row.firstName} {row.lastName}
                                                    </td>
                                                    <td>{row.email || "—"}</td>
                                                    <td>{row.phone || "—"}</td>
                                                    <td>
                                                        {enrolmentStatus === "mapped" && (
                                                            <span className="import-enrolment-badge import-enrolment-mapped">
                                                                <Icon path={mdiAccountCheck} size={0.6} />
                                                                {availableClasses.find(
                                                                    c => c.id === dict[row.classCode?.toLowerCase()]
                                                                )?.name || "Enrolled"}
                                                            </span>
                                                        )}
                                                        {enrolmentStatus === "unmapped" && (
                                                            <span className="import-enrolment-badge import-enrolment-unmapped">
                                                                <Icon path={mdiAlertOutline} size={0.6} />
                                                                {row.classCode} not mapped
                                                                <select
                                                                    className="import-inline-class-select"
                                                                    value=""
                                                                    onChange={e => {
                                                                        if (!e.target.value) return;
                                                                        setMappings(prev => {
                                                                            const exists = prev.find(
                                                                                m =>
                                                                                    m.schoolCode.toLowerCase() ===
                                                                                    row.classCode.toLowerCase()
                                                                            );
                                                                            if (exists) {
                                                                                return prev.map(m =>
                                                                                    m.schoolCode.toLowerCase() ===
                                                                                    row.classCode.toLowerCase()
                                                                                        ? {
                                                                                              ...m,
                                                                                              classId: e.target.value,
                                                                                          }
                                                                                        : m
                                                                                );
                                                                            }
                                                                            return [
                                                                                ...prev,
                                                                                {
                                                                                    schoolCode: row.classCode,
                                                                                    classId: e.target.value,
                                                                                },
                                                                            ];
                                                                        });
                                                                    }}>
                                                                    <option value="">Assign to class...</option>
                                                                    {availableClasses.map(cls => (
                                                                        <option key={cls.id} value={cls.id}>
                                                                            {cls.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </span>
                                                        )}
                                                        {enrolmentStatus === "no-code" && (
                                                            <span className="import-enrolment-badge import-enrolment-none">
                                                                Profile only
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {row.status === "new" && (
                                                            <span className="import-status-badge import-status-new">
                                                                <Icon path={mdiCheck} size={0.6} /> New
                                                            </span>
                                                        )}
                                                        {row.status === "duplicate" && !isDlAccountRow(row) && (
                                                            <span className="import-status-badge import-status-duplicate">
                                                                <Icon path={mdiClose} size={0.6} /> Already exists
                                                            </span>
                                                        )}
                                                        {isDlAccountRow(row) && (
                                                            <span className="import-status-badge import-status-dl-account">
                                                                <Icon path={mdiCheck} size={0.6} /> Has DL account
                                                            </span>
                                                        )}
                                                        {row.status === "probable-duplicate" && (
                                                            <span className="import-status-badge import-status-probable">
                                                                <Icon path={mdiAlertOutline} size={0.6} /> Possible
                                                                duplicate
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {step === "importing" && (
                        <div className="content-card import-importing-card">
                            <p>Importing students...</p>
                        </div>
                    )}

                    {step === "results" && results && (
                        <div className="content-card import-step-card import-results-card">
                            <h2>Import Complete</h2>
                            <div className="import-results-stats">
                                <div className="import-results-stat">
                                    <span className="stat-number" style={{ color: "var(--dl-color-greentick)" }}>
                                        {results.importedCount}
                                    </span>
                                    <span className="stat-label">imported</span>
                                </div>
                                {results.enrolledCount > 0 && (
                                    <div className="import-results-stat">
                                        <span
                                            className="stat-number"
                                            style={{ color: "var(--dl-color-turquoise-default)" }}>
                                            {results.enrolledCount}
                                        </span>
                                        <span className="stat-label">enrolled</span>
                                    </div>
                                )}
                                <div className="import-results-stat">
                                    <span className="stat-number" style={{ color: "var(--dl-color-gray-600)" }}>
                                        {results.skippedCount}
                                    </span>
                                    <span className="stat-label">skipped</span>
                                </div>
                            </div>
                            {results.unmappedCodes && results.unmappedCodes.length > 0 && (
                                <div className="import-unmapped-warning">
                                    <Icon path={mdiAlertOutline} size={0.8} />
                                    {results.unmappedCodes.length} class code
                                    {results.unmappedCodes.length !== 1 ? "s" : ""} not matched:{" "}
                                    {results.unmappedCodes.join(", ")}. These students were imported as profiles only.
                                </div>
                            )}
                            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 8 }}>
                                <button
                                    className="btn-turquoise"
                                    onClick={() => {
                                        setStep("mapping");
                                        setParsedRows([]);
                                        setCheckedRows([]);
                                        setResults(null);
                                        setCsvHasClassCode(false);
                                        setMappings([{ schoolCode: "", classId: "" }]);
                                    }}>
                                    Import more students
                                </button>
                                <button className="btn-purple" onClick={() => navigate("/classes/students")}>
                                    View Students
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Skipped mapping but CSV has class codes */}
            <BaseModal
                isOpen={showMappingModal}
                onClose={handleMappingModalBack}
                title="Your CSV has class codes"
                icon={mdiAlertOutline}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={handleMappingModalBack}>
                            ← Go back to Step 1
                        </button>
                        <button className="base-modal-save-btn" onClick={handleMappingModalContinue}>
                            Continue anyway
                        </button>
                    </>
                }>
                <p className="import-invite-modal-text">
                    Your CSV has a <strong>class_code</strong> column but you skipped the mapping step, so those codes
                    won't be matched to classes automatically.
                </p>
                <p className="import-invite-modal-text">
                    Go back to Step 1 to map your codes now, or continue and assign classes row by row in the preview.
                </p>
            </BaseModal>

            {/* Pressed "Continue with these mappings" but no codes were entered */}
            <BaseModal
                isOpen={showEmptyMappingModal}
                onClose={() => setShowEmptyMappingModal(false)}
                title="No class codes added"
                icon={mdiAlertOutline}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setShowEmptyMappingModal(false)}>
                            Go back and add codes
                        </button>
                        <button
                            className="base-modal-save-btn"
                            onClick={() => {
                                setShowEmptyMappingModal(false);
                                setStep("upload");
                            }}>
                            Skip mapping
                        </button>
                    </>
                }>
                <p className="import-invite-modal-text">
                    You haven't added any class codes yet. Each code needs a matching class selected before you can
                    continue with mappings.
                </p>
                <p className="import-invite-modal-text">
                    Go back and add at least one code, or skip mapping and enrol students separately later.
                </p>
            </BaseModal>

            {/* Post-import invite preview — Mode 2b: studentInvite is locked
                (system-controlled), so admins see what will go out + the
                recipient count, no editable fields. */}
            {showInviteModal && (
                <EmailReviewModal
                    isOpen={showInviteModal}
                    emailType="studentInvite"
                    templateData={{ firstName: "Sample" }}
                    recipientCount={invitableProfileIds.length}
                    onConfirm={handleSendImportInvites}
                    onCancel={() => {
                        setShowInviteModal(false);
                        setInvitableProfileIds([]);
                    }}
                    isConfirming={isConfirmingBulkInvite}
                />
            )}
        </div>
    );
};

export default ClassStudentImportPage;
